'use client';

import { useCart } from '@/features/checkout/CartProvider';
import Rating from '@/features/primitives/Rating';
import { scrollToTopInstantly } from '@/lib/navigation/scroll';
import { getProductDiscount } from '@/lib/productDiscounts';
import {
  formatCurrencyAmount,
  getProductCurrency,
  getProductImage,
} from '@/lib/products';
import { motion, useReducedMotion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Card } from 'react-bootstrap';
import { FaOpencart } from 'react-icons/fa';
import { toast } from 'react-toastify';
import './productCard.scss';

function readMessage(messages, path, fallback = '') {
  const resolved = path
    .split('.')
    .reduce((current, segment) => current?.[segment], messages);

  return typeof resolved === 'string' ? resolved : fallback;
}

function fillTemplate(template, replacements = {}) {
  return Object.entries(replacements).reduce(
    (value, [key, replacement]) =>
      value.replaceAll(`{{${key}}}`, String(replacement ?? '')),
    String(template || ''),
  );
}

function getCategoryName(product) {
  const candidates = [
    product?.primaryCategory,
    Array.isArray(product?.categories) ? product.categories[0] : null,
    product?.category,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }

    if (Array.isArray(candidate)) {
      const nested = candidate.find(
        (entry) =>
          (typeof entry === 'string' && entry.trim()) ||
          (typeof entry?.name === 'string' && entry.name.trim()),
      );

      if (typeof nested === 'string') {
        return nested.trim();
      }

      if (typeof nested?.name === 'string') {
        return nested.name.trim();
      }
    }

    if (typeof candidate?.name === 'string' && candidate.name.trim()) {
      return candidate.name.trim();
    }
  }

  return '';
}

export default function ProductCard({
  product,
  activeCoupons = [],
  disableEntranceMotion = false,
  showAddToCartButton = true,
  showCategoryBadge = false,
  showRatingText = true,
  locale = 'en',
  messages = {},
}) {
  const { addItem } = useCart();
  const router = useRouter();
  const productHref = `/product/${product.productNumber}`;
  const image = getProductImage(product);
  const currency = getProductCurrency(product);
  const discountInfo = getProductDiscount(product, activeCoupons);
  const categoryName = getCategoryName(product);
  const prefersReducedMotion = useReducedMotion();
  const [imgLoaded, setImgLoaded] = useState(false);
  const [overlayPhase, setOverlayPhase] = useState('full');
  const [isAddToCartPulsing, setIsAddToCartPulsing] = useState(false);
  const addToCartTimerRef = useRef(null);
  const addToCartResetTimerRef = useRef(null);
  const formattedPrice = formatCurrencyAmount(product?.price, currency, locale);
  const discountedPrice = discountInfo.hasDiscount
    ? formatCurrencyAmount(discountInfo.discountedPrice, currency, locale)
    : null;
  const savingsLabel =
    discountInfo.discountType === 'percentage'
      ? `-${discountInfo.discountValue}%`
      : `-${formatCurrencyAmount(discountInfo.discountAmount, currency, locale)}`;

  const handleImgLoad = useCallback(
    (event) => {
      const img = event.currentTarget;

      if (imgLoaded) {
        return;
      }

      setOverlayPhase('prefade');

      try {
        img
          .decode?.()
          ?.then(() => {
            setOverlayPhase('fadeout');
            setTimeout(() => setImgLoaded(true), 420);
          })
          ?.catch(() => {
            setOverlayPhase('fadeout');
            setTimeout(() => setImgLoaded(true), 360);
          });
      } catch {
        setOverlayPhase('fadeout');
        setTimeout(() => setImgLoaded(true), 360);
      }
    },
    [imgLoaded],
  );

  const addToCartHandler = () => {
    addItem(product, 1);
    setIsAddToCartPulsing(true);
    window.clearTimeout(addToCartTimerRef.current);
    window.clearTimeout(addToCartResetTimerRef.current);
    toast.success(
      fillTemplate(
        readMessage(messages, 'addedToCartToast', '{{name}} added to cart.'),
        { name: product.name },
      ),
    );
    addToCartTimerRef.current = window.setTimeout(() => {
      router.push('/cart');
    }, 120);
    addToCartResetTimerRef.current = window.setTimeout(() => {
      setIsAddToCartPulsing(false);
    }, 320);
  };

  useEffect(() => {
    return () => {
      window.clearTimeout(addToCartTimerRef.current);
      window.clearTimeout(addToCartResetTimerRef.current);
    };
  }, []);

  const cardVariants = {
    hidden: { opacity: 0, y: 12, scale: 0.98 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.38, ease: 'easeOut' },
    },
    exit: { opacity: 0, y: 10, scale: 0.98, transition: { duration: 0.25 } },
  };

  const Wrapper = disableEntranceMotion ? 'div' : motion.div;
  const wrapperProps = disableEntranceMotion
    ? { className: 'w-100' }
    : {
        variants: prefersReducedMotion ? undefined : cardVariants,
        initial: prefersReducedMotion ? false : 'hidden',
        animate: prefersReducedMotion ? false : 'visible',
        exit: prefersReducedMotion ? false : 'exit',
        className: 'w-100',
      };
  const showRelatedMeta = showCategoryBadge && Boolean(categoryName);
  const priceUnavailableLabel = readMessage(
    messages,
    'priceUnavailable',
    'Price unavailable',
  );

  return (
    <Wrapper {...wrapperProps}>
      <Card
        className={`product-card shadow-none border-0${showAddToCartButton ? '' : ' product-card--related'}`}
      >
        {discountInfo.hasDiscount ? (
          <div className="promo-ribbon" aria-label="Active discount">
            <span className="emoji" aria-hidden>
              {discountInfo.couponLabel}
            </span>
            <span className="text-danger">{savingsLabel}</span>
          </div>
        ) : null}

        <Link
          href={productHref}
          scroll
          className="card-image"
          prefetch
          onClick={scrollToTopInstantly}
          onMouseEnter={() => router.prefetch(productHref)}
          onFocus={() => router.prefetch(productHref)}
        >
          <div className="image-wrapper">
            <Image
              src={image || '/icons/store.png'}
              alt={product.name}
              fill
              sizes="(min-width: 1400px) 240px, (min-width: 1200px) 22vw, (min-width: 768px) 28vw, 70vw"
              onLoad={handleImgLoad}
              className={!imgLoaded ? 'loading-blur' : ''}
            />

            {!imgLoaded ? (
              <motion.div
                className="image-skeleton-overlay"
                initial={{ opacity: 1 }}
                animate={
                  overlayPhase === 'full'
                    ? { opacity: 1 }
                    : overlayPhase === 'prefade'
                      ? { opacity: 0.5 }
                      : { opacity: 0 }
                }
                transition={{ duration: 0.4, ease: 'easeOut' }}
              >
                <div className="skel-rect" />
              </motion.div>
            ) : null}
          </div>
        </Link>

        <Card.Body>
          <Link
            href={productHref}
            scroll
            className="text-decoration-none"
            prefetch
            onClick={scrollToTopInstantly}
            onMouseEnter={() => router.prefetch(productHref)}
            onFocus={() => router.prefetch(productHref)}
          >
            <Card.Title as="p" className="product-title">
              <strong>{product.name}</strong>
            </Card.Title>
          </Link>

          <Card.Text as="div" className="rating-block">
            <Rating
              value={product.rating}
              numReviews={product.numReviews}
              showText={showRatingText}
              messages={messages}
            />
          </Card.Text>

          <div
            className={`product-card__meta${showRelatedMeta ? ' product-card__meta--split' : ''}`}
          >
            {discountInfo.hasDiscount ? (
              <div className="price-container">
                <Card.Text as="h5" className="price price-control mb-1">
                  <span className="discounted-price">
                    {discountedPrice || priceUnavailableLabel}
                  </span>
                  <del className="original-price fs-7">
                    {formattedPrice || priceUnavailableLabel}
                  </del>
                </Card.Text>
              </div>
            ) : (
              <Card.Text as="h5" className="price mb-0">
                {formattedPrice || priceUnavailableLabel}
              </Card.Text>
            )}

            {showRelatedMeta ? (
              <Badge bg="info" pill className="product-card__category-badge">
                {categoryName}
              </Badge>
            ) : null}
          </div>

          {showAddToCartButton ? (
            <Button
              variant="secondary"
              className={`add-to-cart-btn rounded-pill${isAddToCartPulsing ? ' add-to-cart-btn--pulsing' : ''}`}
              onClick={addToCartHandler}
              disabled={isAddToCartPulsing}
              aria-label={fillTemplate(
                readMessage(messages, 'addToCartAria', 'Add {{name}} to cart'),
                { name: product.name },
              )}
            >
              <span className="me-2">
                {readMessage(messages, 'addToCart', 'Add to Cart')}
              </span>
              <FaOpencart size={22} aria-hidden="true" />
            </Button>
          ) : null}
        </Card.Body>
      </Card>
    </Wrapper>
  );
}
